import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import BranchData from './BranchData';
import { 
  FaCalendarAlt, 
  FaClock, 
  FaExclamationTriangle,
  FaCheckCircle,
  FaUser,
  FaMapMarkerAlt
} from 'react-icons/fa';
import { getRecentActivities } from '../store/statsSlice';

// Location display fix for live server deployment - v2.1
const DashboardOverview = ({ userRole = 'Admin' }) => {
  const dispatch = useDispatch();
  const { recentActivities, activitiesLoading } = useSelector((state) => state.stats);
  const { userInfo } = useSelector((state) => state.auth);
  const [recentLogins, setRecentLogins] = React.useState([]);
  const [loginsLoading, setLoginsLoading] = React.useState(true);

  useEffect(() => {
    // Fetch actual recent activities from API
    dispatch(getRecentActivities());
    
    // Fetch recent logins
    fetchRecentLogins();
  }, [dispatch, userRole]);

  const fetchRecentLogins = async () => {
    try {
      setLoginsLoading(true);
      const response = await fetch(`/api/login-tracking/recent?limit=5&userRole=${userRole}`, {
        headers: {
          'Authorization': `Bearer ${userInfo?.token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('🔍 Recent logins data received:', JSON.stringify(data.data, null, 2));
        
        // Debug location data specifically
        if (data.data && data.data.length > 0) {
          data.data.forEach((login, index) => {
            console.log(`📍 Login ${index + 1} location data:`, JSON.stringify(login.location, null, 2));
          });
        }
        
        setRecentLogins(data.data || []);
      } else {
        console.error('Failed to fetch recent logins');
        setRecentLogins([]);
      }
    } catch (error) {
      console.error('Error fetching recent logins:', error);
      setRecentLogins([]);
    } finally {
      setLoginsLoading(false);
    }
  };

  // Helper function to format time
  const formatTime = (dateString) => {
    const now = new Date();
    const activityTime = new Date(dateString);
    const diffInMinutes = Math.floor((now - activityTime) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes} minute${diffInMinutes > 1 ? 's' : ''} ago`;
    
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`;
    
    const diffInDays = Math.floor(diffInHours / 24);
    return `${diffInDays} day${diffInDays > 1 ? 's' : ''} ago`;
  };

  // Helper function to get status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'success': return 'bg-green-100 text-green-800 border-green-200';
      case 'failed': return 'bg-red-100 text-red-800 border-red-200';
      case 'blocked': return 'bg-orange-100 text-orange-800 border-orange-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'bg-red-100 text-red-800 border-red-200';
      case 'medium': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'low': return 'bg-green-100 text-green-800 border-green-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  return (
    <div className="space-y-8">


      {/* Branch Data */}
      <BranchData userRole={userRole} />

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Recent Activities */}
        <div className="bg-white rounded-xl shadow-lg p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-gray-800">Recent Activities</h2>
            <FaClock className="text-gray-400" />
          </div>
          
          <div className="space-y-4">
            {activitiesLoading ? (
              // Loading skeleton
              Array.from({ length: 3 }).map((_, index) => (
                <div key={index} className="animate-pulse flex items-start space-x-3 p-3">
                  <div className="w-10 h-10 bg-gray-200 rounded-full"></div>
                  <div className="flex-1">
                    <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                    <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                  </div>
                </div>
              ))
            ) : recentActivities.length > 0 ? (
              recentActivities.map((activity) => (
                <div key={activity.id} className="flex items-start space-x-3 p-3 hover:bg-gray-50 rounded-lg transition-colors">
                  <div className="p-2 rounded-full bg-green-100">
                    <FaCheckCircle className="text-lg text-green-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-sm font-medium text-gray-800 truncate">
                        {activity.user} sold {activity.service}
                      </p>
                      <span className="text-xs text-gray-500 whitespace-nowrap ml-2">
                        <FaMapMarkerAlt className="inline mr-1" />
                        {activity.branch} Branch • {formatTime(activity.time)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-600">Sale Amount</span>
                      <span className="text-sm font-bold text-green-600">
                        ₹{activity.amount.toLocaleString()}
                      </span>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8">
                <FaClock className="text-4xl text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">No recent activities</p>
                <p className="text-sm text-gray-400 mt-1">Sales activities will appear here</p>
              </div>
            )}
          </div>
          
          <button className="w-full mt-4 text-blue-600 hover:text-blue-800 font-medium text-sm py-2">
            View All Activities →
          </button>
        </div>

        {/* Recent Logins */}
        <div className="bg-white rounded-xl shadow-lg p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-gray-800">Recent Logins</h2>
            <FaUser className="text-gray-400" />
          </div>
          
          <div className="space-y-4">
            {loginsLoading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-3"></div>
                <p className="text-gray-500">Loading recent logins...</p>
              </div>
            ) : recentLogins.length > 0 ? (
              recentLogins.map((login) => (
                <div key={login.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center space-x-3">
                      <div className={`w-3 h-3 rounded-full ${
                        login.status === 'success' ? 'bg-green-500' : 
                        login.status === 'failed' ? 'bg-red-500' : 
                        login.status === 'blocked' ? 'bg-orange-500' : 'bg-gray-400'
                      }`}></div>
                      <div>
                        <h3 className="font-medium text-gray-800">{login.user}</h3>
                        <p className="text-sm text-gray-600">{login.email}</p>
                      </div>
                    </div>
                    <div className="flex flex-col items-end space-y-1">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        login.role === 'Admin' 
                          ? 'bg-purple-100 text-purple-800 border border-purple-200'
                          : 'bg-blue-100 text-blue-800 border border-blue-200'
                      }`}>
                        {login.role}
                      </span>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(login.status)}`}>
                        {login.status}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between text-sm text-gray-600 mt-2">
                    <span className="flex items-center">
                      <FaClock className="mr-1" />
                      {formatTime(login.loginTime)}
                    </span>
                    <span className="flex items-center" title={login.location?.fullAddress || 'Location information'}>
                      <FaMapMarkerAlt className="mr-1" />
                      {(() => {
                        const loc = login.location;
                        
                        // Debug: Log location data for live server debugging
                        if (process.env.NODE_ENV === 'development') {
                          console.log('Location data:', loc);
                        }
                        
                        // Priority 1: Use fullAddress if available (complete database address)
                        if (loc?.fullAddress && 
                            loc.fullAddress !== 'Unknown Location' && 
                            loc.fullAddress !== 'Unknown, Unknown' &&
                            loc.fullAddress !== 'Development Environment' &&
                            !loc.fullAddress.includes('Unknown')) {
                          return loc.fullAddress;
                        }
                        
                        // Priority 2: Use detailedAddress if available
                        if (loc?.detailedAddress && 
                            loc.detailedAddress !== 'Unknown Location' && 
                            loc.detailedAddress !== 'Unknown, Unknown' &&
                            loc.detailedAddress !== 'Development Environment' &&
                            !loc.detailedAddress.includes('Unknown')) {
                          return loc.detailedAddress;
                        }
                        
                        // Priority 3: Build detailed address from components
                        if (loc?.city && loc?.region && 
                            loc.city !== 'Unknown' && loc.region !== 'Unknown' &&
                            loc.city !== 'Development' && loc.region !== 'Local') {
                          const parts = [];
                          if (loc.city) parts.push(loc.city);
                          if (loc.region && loc.region !== loc.city) parts.push(loc.region);
                          if (loc.countryName && loc.countryName !== 'Unknown') parts.push(loc.countryName);
                          if (loc.zip) parts.push(loc.zip);
                          return parts.join(', ');
                        }
                        
                        // Priority 4: City with country (handle country codes)
                        if (loc?.city && loc.city !== 'Unknown' && loc.city !== 'Development') {
                          const country = loc.countryName || (loc.country === 'IN' ? 'India' : loc.country);
                          const cityCountry = country && country !== 'Unknown' ? `${loc.city}, ${country}` : loc.city;
                          return loc.zip ? `${cityCountry}, ${loc.zip}` : cityCountry;
                        }
                        
                        // Priority 5: Country name or code with ZIP
                        if (loc?.countryName && loc.countryName !== 'Unknown') {
                          return loc.zip ? `${loc.countryName}, ${loc.zip}` : loc.countryName;
                        }
                        
                        if (loc?.country && loc.country !== 'Unknown') {
                          const countryName = loc.country === 'IN' ? 'India' : loc.country;
                          return loc.zip ? `${countryName}, ${loc.zip}` : countryName;
                        }
                        
                        // Priority 6: Try to use any available location fields
                        const availableFields = [];
                        if (loc?.city && loc.city !== 'Unknown') availableFields.push(loc.city);
                        if (loc?.region && loc.region !== 'Unknown' && loc.region !== loc?.city) availableFields.push(loc.region);
                        if (loc?.country && loc.country !== 'Unknown') {
                          availableFields.push(loc.country === 'IN' ? 'India' : loc.country);
                        }
                        if (loc?.zip) availableFields.push(loc.zip);
                        
                        if (availableFields.length > 0) {
                          return availableFields.join(', ');
                        }
                        
                        // Final fallback
                        return 'Location Unknown';
                      })()}
                      {login.location?.source && (
                        <span className={`ml-2 px-1 py-0.5 text-xs rounded ${
                          login.location.source === 'GPS' 
                            ? 'bg-green-100 text-green-700' 
                            : 'bg-blue-100 text-blue-700'
                        }`}>
                          {login.location.source}
                        </span>
                      )}
                    </span>
                  </div>
                  <div className="mt-2 text-xs text-gray-500">
                    IP: {login.ipAddress} • Mobile: {login.mobileNumber}
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8">
                <FaUser className="text-4xl text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">No recent logins</p>
                <p className="text-sm text-gray-400 mt-1">Login activities will appear here</p>
              </div>
            )}
          </div>
          
          <button className="w-full mt-4 text-blue-600 hover:text-blue-800 font-medium text-sm py-2">
            View All Logins →
          </button>
        </div>
      </div>


    </div>
  );
};

export default DashboardOverview;
